import {
  isEmbeddedContext,
  requestEmbedHeightSync,
  scrollEmbedToTop,
} from '@/utils/embeddedUtils';

export type PaymentSuccessIconVariant = 'green' | 'blue';

export interface PaymentSuccessModalOptions {
  title: string;
  message: string;
  iconVariant?: PaymentSuccessIconVariant;
  onContinue: () => void;
}

const ICON_STYLES: Record<PaymentSuccessIconVariant, { bg: string; fill: string }> = {
  green: {
    bg: 'rgba(72, 187, 120, 0.1)',
    fill: '#48bb78',
  },
  blue: {
    bg: 'rgba(66, 153, 225, 0.1)',
    fill: '#4299e1',
  },
};

function getBackdropStyles(isEmbedded: boolean): string {
  if (isEmbedded) {
    return `
      position: absolute;
      left: 0;
      width: 100%;
      display: flex;
      justify-content: center;
      background-color: transparent;
      z-index: 9999;
      padding: 20px;
      box-sizing: border-box;
      pointer-events: none;
    `;
  }

  return `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    padding: 20px;
    box-sizing: border-box;
  `;
}

function getEmbedBackdropTop(): number {
  return 24;
}

function scrollEmbedToSuccessModal(modalEl: HTMLElement): void {
  window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;

  scrollEmbedToTop('payment-success-modal');

  requestAnimationFrame(() => {
    modalEl.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'instant' });
    scrollEmbedToTop('payment-success-modal-mounted');
  });

  // Retry after iframe height sync settles in the parent embed script
  window.setTimeout(() => {
    modalEl.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'instant' });
    scrollEmbedToTop('payment-success-modal-settled');
  }, 350);
}

export function showPaymentSuccessModal(options: PaymentSuccessModalOptions): void {
  const {
    title,
    message,
    iconVariant = 'green',
    onContinue,
  } = options;

  const isEmbedded = isEmbeddedContext();

  const backdrop = document.createElement('div');
  backdrop.setAttribute('data-spotlight-success-modal', 'true');
  backdrop.style.cssText = getBackdropStyles(isEmbedded);

  if (isEmbedded) {
    backdrop.style.top = `${getEmbedBackdropTop()}px`;
  }

  const modalContent = document.createElement('div');
  modalContent.style.cssText = `
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    padding: 40px;
    text-align: center;
    max-width: 90%;
    width: 450px;
    position: relative;
    ${isEmbedded ? 'pointer-events: auto;' : ''}
  `;

  if (isEmbedded) {
    modalContent.style.boxShadow = '0 10px 40px rgba(0, 0, 0, 0.18)';
  }

  const iconStyles = ICON_STYLES[iconVariant];
  const successIcon = document.createElement('div');
  successIcon.style.cssText = `
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: ${iconStyles.bg};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px auto;
  `;

  successIcon.innerHTML = `<svg width="40" height="40" viewBox="0 0 24 24" fill="${iconStyles.fill}">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"></path>
  </svg>`;

  const titleEl = document.createElement('h2');
  titleEl.textContent = title;
  titleEl.style.cssText = `
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 16px 0;
    color: #2d3748;
  `;

  const messageEl = document.createElement('p');
  messageEl.textContent = message;
  messageEl.style.cssText = `
    font-size: 16px;
    line-height: 1.5;
    color: #718096;
    margin: 0 0 32px 0;
  `;

  const button = document.createElement('button');
  button.textContent = 'Continue';
  button.style.cssText = `
    background-color: #1ebeaf;
    color: white;
    border: none;
    border-radius: 50px;
    padding: 12px 32px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
  `;

  button.addEventListener('mouseenter', () => {
    button.style.backgroundColor = '#19a89a';
  });

  button.addEventListener('mouseleave', () => {
    button.style.backgroundColor = '#1ebeaf';
  });

  const closeModal = () => {
    if (backdrop.parentNode) {
      document.body.removeChild(backdrop);
    }
    onContinue();
  };

  button.addEventListener('click', closeModal);

  modalContent.appendChild(successIcon);
  modalContent.appendChild(titleEl);
  modalContent.appendChild(messageEl);
  modalContent.appendChild(button);
  backdrop.appendChild(modalContent);

  document.body.appendChild(backdrop);

  if (isEmbedded) {
    scrollEmbedToSuccessModal(modalContent);
    requestEmbedHeightSync();
    window.setTimeout(() => requestEmbedHeightSync(), 350);
  }
}
